/**
 * AP-34 Nachtrag 1, 8.2 (AP-40) — **Dashboard der Eingangsrechnungen**, die Startseite des
 * Hauptmenüpunkts (`/incoming-invoices/dashboard`). Jede Kachel ist ein Filter der Liste dahinter —
 * der Server zählt über denselben Filter, mit dem die Liste lädt. Fehlt das Recht, fehlt die Kachel.
 */
import { Link } from 'react-router-dom'
import { Receipt } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { DashboardKopf, DashboardListe, Kachel, KachelRaster, useDashboard } from '@/components/dashboard'
import { pageWide } from '@/lib/form-styles'
import { fetchIncomingDashboard } from '../api/incoming-invoices.api'
import { tagText } from '../lib/texte'

interface Faellig {
  id: string
  number: string | null
  supplierName: string | null
  dueDate: string
  openAmount: number
  overdue: boolean
}
interface Freigabe {
  id: string
  number: string | null
  supplierName: string | null
  grossTotal: number
  invoiceDate: string | null
}

export default function IncomingDashboardPage() {
  const { bereich, setBereich, daten, fehler, kachel } = useDashboard('incoming_invoices', fetchIncomingDashboard)
  const betrag = (key: string, zusatz: string) => {
    const b = kachel(key)?.amount
    return typeof b === 'number' ? `${geldText(b)} ${zusatz}` : undefined
  }

  return (
    <div className={pageWide}>
      <DashboardKopf
        symbol={Receipt}
        titel="Eingangsrechnungen"
        untertitel="Was zu erfassen, freizugeben, zu zahlen und zu übergeben ist."
        bereich={bereich}
        onBereich={setBereich}
        umschalter={daten?.scopeSwitch === true}
        beschriftung={{ all: 'alle', mine: 'meine' }}
        stand={daten?.stand}
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <KachelRaster>
        <Kachel titel="Im Eingangsstapel" kachel={kachel('uncaptured')} hinweis="hochgeladen, noch ohne Lieferant" />
        <Kachel titel="In Erfassung" kachel={kachel('drafts')} hinweis="Entwürfe und abgelehnte" />
        <Kachel titel="Meine Freigaben" ton="warnung" kachel={kachel('myApprovals')} hinweis="warten auf dich" />
        <Kachel titel="Im Freigabelauf" kachel={kachel('inApproval')} hinweis={betrag('inApproval', 'brutto')} />
        <Kachel titel="Fällig in 7 Tagen" kachel={kachel('dueSoon')} hinweis={betrag('dueSoon', 'offen')} />
        <Kachel titel="Überfällig" ton="gefahr" kachel={kachel('overdue')} hinweis={betrag('overdue', 'offen')} />
        <Kachel titel="Skonto läuft ab" ton="warnung" kachel={kachel('discountExpiring')} hinweis={betrag('discountExpiring', 'Skonto')} />
        <Kachel titel="Zahlung gesperrt" ton="warnung" kachel={kachel('paymentBlocked')} />
        <Kachel titel="Zu übergeben" kachel={kachel('toExport')} hinweis="freigegeben, noch nicht an die Buchhaltung" />
        <Kachel titel="Rücklässe fällig" kachel={kachel('retentionsDue')} hinweis={betrag('retentionsDue', 'in 30 Tagen')} />
        <Kachel titel="Gemeinkosten diesen Monat" kachel={kachel('overheadMonth')} hinweis={betrag('overheadMonth', 'netto')} />
      </KachelRaster>

      <div className="grid gap-4 lg:grid-cols-2">
        <DashboardListe<Faellig>
          titel="Nächste Fälligkeiten"
          zeilen={daten?.lists?.due as Faellig[] | undefined}
          schluessel={(z) => z.id}
          alle="/incoming-invoices/open-items"
          leer="Nichts offen."
          zeile={(z) => (
            <>
              <span className={`w-20 shrink-0 text-xs ${z.overdue ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>{tagText(z.dueDate)}</span>
              <Link to={`/incoming-invoices/${z.id}`} className="min-w-0 flex-1 truncate hover:underline">
                <span className="mr-2 font-mono text-xs text-muted-foreground">{z.number ?? ''}</span>
                {z.supplierName ?? '—'}
              </Link>
              <span className="shrink-0 text-xs tabular-nums">{geldText(z.openAmount)}</span>
            </>
          )}
        />
        {daten?.tiles?.myApprovals && (
          <DashboardListe<Freigabe>
            titel="Meine Freigaben"
            zeilen={daten?.lists?.approvals as Freigabe[] | undefined}
            schluessel={(z) => z.id}
            alle="/incoming-invoices/approvals"
            leer="Nichts wartet auf deine Freigabe."
            zeile={(z) => (
              <>
                <span className="w-20 shrink-0 text-xs text-muted-foreground">{tagText(z.invoiceDate)}</span>
                <Link to={`/incoming-invoices/${z.id}`} className="min-w-0 flex-1 truncate hover:underline">
                  <span className="mr-2 font-mono text-xs text-muted-foreground">{z.number ?? ''}</span>
                  {z.supplierName ?? '—'}
                </Link>
                <span className="shrink-0 text-xs tabular-nums">{geldText(z.grossTotal)}</span>
              </>
            )}
          />
        )}
      </div>
    </div>
  )
}
